// src/components/Timesheet.js
import React, { useState, useEffect } from "react";
import classNames from "classnames";

// Constants for days and hours
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const HOURS = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}:00`);

// Activity definitions
const ACTIVITY_CODES = [
  { code: "EXE001", label: "EY work task (client deliverable)", category: "Execution", color: "bg-blue-600" },
  { code: "EXE002", label: "Startup building (Axion/Neurafate dev)", category: "Execution", color: "bg-blue-600" },
  { code: "EXE003", label: "Planning (sprints, goals, backlog)", category: "Execution", color: "bg-blue-600" },
  { code: "EXE004", label: "Business ops (registration, taxes, etc.)", category: "Execution", color: "bg-blue-600" },
  { code: "EXE005", label: "Outreach/networking (calls, emails)", category: "Execution", color: "bg-blue-600" },
  { code: "BDY001", label: "Workout (gym/home)", category: "Body", color: "bg-red-600" },
  { code: "BDY002", label: "Meal prep or eating", category: "Body", color: "bg-red-600" },
  { code: "BDY003", label: "Hygiene (shower, grooming)", category: "Body", color: "bg-red-600" },
  { code: "BDY004", label: "Walk/stretch/movement break", category: "Body", color: "bg-red-600" },
  { code: "BDY005", label: "Sleep (track if needed)", category: "Body", color: "bg-red-600" },
  { code: "MND001", label: "Meditation", category: "Mind", color: "bg-indigo-600" },
  { code: "MND002", label: "Journaling (text or voice)", category: "Mind", color: "bg-indigo-600" },
  { code: "MND003", label: "Therapy or mental review", category: "Mind", color: "bg-indigo-600" },
  { code: "MND004", label: "Mood tracking", category: "Mind", color: "bg-indigo-600" },
  { code: "MND005", label: "Visualization/future mapping", category: "Mind", color: "bg-indigo-600" },
  { code: "CLN001", label: "Physical space cleanup", category: "Cleanup", color: "bg-yellow-600" },
  { code: "CLN002", label: "Digital file sorting", category: "Cleanup", color: "bg-yellow-600" },
  { code: "CLN003", label: "Email/inbox cleaning", category: "Cleanup", color: "bg-yellow-600" },
  { code: "CLN004", label: "App/data hygiene (resets, uninstalling)", category: "Cleanup", color: "bg-yellow-600" },
  { code: "INP001", label: "Reading (books/articles)", category: "Input", color: "bg-green-600" },
  { code: "INP002", label: "Courses/tutorials", category: "Input", color: "bg-green-600" },
  { code: "INP003", label: "Podcasts/videos", category: "Input", color: "bg-green-600" },
  { code: "INP004", label: "Feedback received (reviews, advice)", category: "Input", color: "bg-green-600" },
  { code: "INP005", label: "Market/tech research", category: "Input", color: "bg-green-600" },
  { code: "OUT001", label: "Writing (creative, strategic)", category: "Output", color: "bg-purple-600" },
  { code: "OUT002", label: "Coding/building", category: "Output", color: "bg-purple-600" },
  { code: "OUT003", label: "Drawing/design", category: "Output", color: "bg-purple-600" },
  { code: "OUT004", label: "Recording/sharing content", category: "Output", color: "bg-purple-600" },
  { code: "OUT005", label: "Publishing/posting", category: "Output", color: "bg-purple-600" },
  { code: "MNT001", label: "Budgeting/finances", category: "Maintenance", color: "bg-orange-600" },
  { code: "MNT002", label: "Task organization/Notion", category: "Maintenance", color: "bg-orange-600" },
  { code: "MNT003", label: "Routine check-ins", category: "Maintenance", color: "bg-orange-600" },
  { code: "MNT004", label: "Device cleaning", category: "Maintenance", color: "bg-orange-600" },
  { code: "MNT005", label: "Life admin (bills, renewals, errands)", category: "Maintenance", color: "bg-orange-600" },
  { code: "RCV001", label: "Intentional rest", category: "Recovery", color: "bg-teal-600" },
  { code: "RCV002", label: "Power nap", category: "Recovery", color: "bg-teal-600" },
  { code: "RCV003", label: "Light entertainment (recovery)", category: "Recovery", color: "bg-teal-600" },
  { code: "RCV004", label: "Relaxation rituals (music, tea, etc.)", category: "Recovery", color: "bg-teal-600" },
  { code: "RCV005", label: "Gaming/Gaming with Friends", category: "Recovery", color: "bg-teal-600" },
  { code: "LEK001", label: "Doomscrolling/social media", category: "Leakage", color: "bg-gray-700" },
  { code: "LEK002", label: "NSFW indulgence (unplanned)", category: "Leakage", color: "bg-gray-700" },
  { code: "LEK003", label: "YouTube binge", category: "Leakage", color: "bg-gray-700" },
  { code: "LEK004", label: "Game time with guilt", category: "Leakage", color: "bg-gray-700" },
  { code: "LEK005", label: "Delay spiral (stalling on everything)", category: "Leakage", color: "bg-gray-700" }
];

// Helper: Get starting Sunday of the week for a given date.
const getStartOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Set to Sunday
  d.setDate(d.getDate() - d.getDay());
  return d;
};

// Helper: Format a Date as YYYY-MM-DD (for week key)
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Returns week identifier string based on the starting Sunday.
const getWeekId = (date) => {
  const sunday = getStartOfWeek(date);
  return formatDate(sunday);
};

const Timesheet = () => {
  // State for the currently selected week (using the week’s starting Sunday)
  const [selectedDate, setSelectedDate] = useState(getStartOfWeek(new Date()));

  // Global state storing all weeks’ entries.
  // Format: { "YYYY-MM-DD": { "dayIndex-hourIndex": "activityCode", ... }, ... }
  const [allEntries, setAllEntries] = useState({});

  // Local copy of entries for the current week.
  const weekId = getWeekId(selectedDate);
  const currentEntries = allEntries[weekId] || {};

  // State for current time (used for locking cells in the current day if on the current week)
  const [currentTime, setCurrentTime] = useState(new Date());

  // Load all entries from localStorage when component mounts.
  useEffect(() => {
    const stored = localStorage.getItem("timesheetEntriesByWeek");
    if (stored) {
      setAllEntries(JSON.parse(stored));
    }
  }, []);

  // Save all entries to localStorage whenever they change.
  useEffect(() => {
    localStorage.setItem("timesheetEntriesByWeek", JSON.stringify(allEntries));
  }, [allEntries]);

  // Update current time every minute.
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Handler to update an entry for the current week.
  const handleSelect = (dayIndex, hourIndex, code) => {
    const key = `${dayIndex}-${hourIndex}`;
    const updatedWeekEntries = { ...currentEntries, [key]: code };
    setAllEntries((prev) => ({
      ...prev,
      [weekId]: updatedWeekEntries,
    }));
  };

  // Check if all previous hours in the day are filled.
  const isPreviousHourFilled = (dayIndex, hourIndex) => {
    for (let i = 0; i < hourIndex; i++) {
      const key = `${dayIndex}-${i}`;
      if (!currentEntries[key]) return false;
    }
    return true;
  };

  // Determine if the timeslot is locked.
  const isLockedHour = (dayIndex, hourIndex) => {
    // Only unlock if viewing current week and current day/hours.
    const today = new Date();
    const currentWeekId = getWeekId(today);
    // If not current week, allow editing.
    if (weekId !== currentWeekId) return false;
    // For current week, lock future hours and hours if previous ones not filled.
    if (dayIndex !== today.getDay()) return true;
    if (hourIndex > today.getHours()) return true;
    return !isPreviousHourFilled(dayIndex, hourIndex);
  };

  // Get next unfilled hour for highlighting on current day.
  const getNextUnfilledHour = () => {
    const today = new Date();
    if (weekId !== getWeekId(today)) return null;
    for (let i = 0; i <= today.getHours(); i++) {
      const key = `${today.getDay()}-${i}`;
      if (!currentEntries[key]) return i;
    }
    return null;
  };

  // Render a cell in the table.
  const renderCell = (dayIndex, hourIndex) => {
    const key = `${dayIndex}-${hourIndex}`;
    const isFilled = currentEntries[key];
    const locked = isLockedHour(dayIndex, hourIndex);
    const nextUnfilledHour = getNextUnfilledHour();
    const shouldHighlight =
      weekId === getWeekId(new Date()) &&
      dayIndex === new Date().getDay() &&
      hourIndex === nextUnfilledHour;
    return (
      <td
        key={key}
        className={classNames(
          "border border-gray-700 p-1 text-sm text-white rounded-md min-w-[75px]",
          ACTIVITY_CODES.find((c) => c.code === currentEntries[key])?.color,
          {
            "bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 animate-pulse": shouldHighlight,
            "bg-gray-950": !isFilled && !shouldHighlight,
            "hover:bg-cyan-700 transition": !locked,
            "opacity-50 cursor-not-allowed": locked,
          }
        )}
      >
        <select
          value={currentEntries[key] || ""}
          onChange={(e) => handleSelect(dayIndex, hourIndex, e.target.value)}
          className="w-full bg-transparent text-white focus:outline-none rounded-md"
          disabled={locked}
        >
          <option value="">--</option>
          {ACTIVITY_CODES.map((code) => (
            <option key={code.code} value={code.code} className="text-black">
              <span className="block md:hidden">{code.code}</span>
              <span className="hidden md:block">{`${code.code} - ${code.label}`}</span>
            </option>
          ))}
        </select>
      </td>
    );
  };

  // Handlers to navigate weeks.
  const goToPreviousWeek = () => {
    const prevWeek = new Date(selectedDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setSelectedDate(getStartOfWeek(prevWeek));
  };

  const goToNextWeek = () => {
    const nextWeek = new Date(selectedDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setSelectedDate(getStartOfWeek(nextWeek));
  };

  // Helper to display the week range.
  const getWeekRangeDisplay = () => {
    const start = getStartOfWeek(selectedDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${formatDate(start)} to ${formatDate(end)}`;
  };

  return (
    <div className="overflow-auto max-w-full bg-gray-950 p-6 rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold text-center text-white mb-4">Mai's Timesheet</h2>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousWeek}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 transition rounded"
        >
          Previous Week
        </button>
        <div className="text-white font-medium">{getWeekRangeDisplay()}</div>
        <button
          onClick={goToNextWeek}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 transition rounded"
        >
          Next Week
        </button>
      </div>
      <div className="w-full overflow-x-auto rounded-xl">
        <table
          className="w-full border-separate rounded-xl table-auto"
          style={{ borderSpacing: "4px" }}
        >
          <thead>
            <tr>
              <th className="bg-gray-950 text-cyan-300 p-2 border border-gray-700 rounded-tl-xl min-w-[60px]">
                Hour
              </th>
              {DAYS.map((day, i, arr) => (
                <th
                  key={day}
                  className={classNames("p-2 border border-gray-700 text-white min-w-[90px]", {
                    "bg-cyan-800": i === new Date().getDay() && weekId === getWeekId(new Date()),
                    "bg-gray-800": i !== new Date().getDay() || weekId !== getWeekId(new Date()),
                    "rounded-tr-xl": i === arr.length - 1,
                  })}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HOURS.map((hour, hourIndex) => (
              <tr key={hour} className="rounded-md">
                <td className="bg-gray-900 text-cyan-400 p-2 border border-gray-700 rounded-md min-w-[60px]">
                  {hour}
                </td>
                {DAYS.map((_, dayIndex) => renderCell(dayIndex, hourIndex))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Timesheet;
